import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { htmlToText } from "html-to-text";
import IoIosShareAlt from "../../assets/icons/share";
import HiDotsVertical from "../../assets/icons/dotsVertical";
import FaBookmark from "../../assets/icons/bookmark";
import FaRegBookmark from "../../assets/icons/bookmarkOutline";
import FaCircleUser from "../../assets/icons/user";
import FaFlag from "../../assets/icons/flag";
import RiReplyFill from "../../assets/icons/arrowBackward";
import FaCommentDots from "../../assets/icons/message";
import TbArrowBigDown from "../../assets/icons/downvoteOutline";
import TbArrowBigDownFilled from "../../assets/icons/downvote";
import TbArrowBigUp from "../../assets/icons/upvoteOutline";
import TbArrowBigUpFilled from "../../assets/icons/upvote";
import { otherUserProfile, privateRoutes, storyDetailsRoute } from "../../routes/routesPath";
import useAuth from "../../hooks/useAuth";
import { format_date, getTimePassed, isArray } from "../../utils/formatersAndParsers";
import { defaultPage, maleAvatarHolder } from "../../utils/constants";
import {
  formatReactionCount,
  getSlug,
  getUserImage,
  handleLoginPop,
  isEmpty,
} from "../../utils/helpers";
import { errorMsg } from "../../utils/toastMessage";
import { handleApiRequest } from "../../services/handleApiRequest";
import {
  addComment,
  addCommentReply,
  getCommentList,
  getRepliesList,
  getStoryDetails,
  manageCommentReaction,
} from "../../redux/stories/thunk";
import { manageBookmark } from "../../redux/bookmarks/thunk";
import { ErrorField } from "../../Component/common/errorField";
import BookmarkSuccess from "../../Component/Modals/bookmarkSuccess";
import ReportStoryPop from "../../Component/Modals/reportStoryPop";
import AddReaction from "../../Component/common/addReaction";
import MyPagination from "../../Component/common/pagination";
import StoryDetailsSkeleton from "../../Component/skeleton/storyDetailsSkeleton";
import SharePop from "../../Component/Modals/sharePop";
import MyHelmet from "../../seo/helmet";

export default function StoryDetails({ initialData }) {
  const maxCommentLength = 1200;
  const maxReplyLength = 850;
  const { isAuthenticated } = useAuth();
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { userProfile = {} } = useSelector((state) => state.auth);

  const [userAction, setUserAction] = useState(null);
  const [paginationDetails, setPaginationDetails] = useState(defaultPage);
  const [inputValue, setInputValue] = useState({});
  const [errors, setErrors] = useState({});
  const [addReply, setAddReply] = useState(null);
  const [commentsList, setCommentsList] = useState({});
  const [storyDetails, setStoryDetails] = useState(
    initialData.storyDetails?._id === storyId ? initialData.storyDetails : {}
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputValue((prev) => ({ ...prev, [name]: value }));
  };

  const handleStoryDetails = async () => {
    if (isEmpty(storyDetails) || initialData.storyDetails?._id === storyId) {
      const handleError = (error) => {
        if (error.statusCode) {
          navigate(-1);
        }
      };
      const response = await handleApiRequest(getStoryDetails, { storyId }, true, handleError);
      if (response.status) {
        setStoryDetails(response.data);
      }
    }
  };

  const handleCommentList = async () => {
    const request = { storyId, ...paginationDetails };
    const response = await handleApiRequest(getCommentList, request);

    if (response.status) {
      setCommentsList(response.data || {});
    }
  };

  const handleAddComment = async () => {
    if (!isAuthenticated) return handleLoginPop(true);
    if (inputValue.comment?.length < 15)
      return setErrors((prev) => ({ ...prev, comment: "Must be atleast 15 charater long" }));
    else if (inputValue.comment?.length > maxCommentLength) {
      return setErrors((prev) => ({
        ...prev,
        comment: `Maximum ${maxCommentLength} characters allowed`,
      }));
    } else {
      setErrors({});
    }

    setUserAction({ action: "showCommentLoader" });
    const request = { storyId, comment: inputValue.comment };
    const response = await handleApiRequest(addComment, request);
    setUserAction(null);

    if (response.status) {
      setStoryDetails((prev) => ({ ...prev, commentsCount: prev.commentsCount + 1 }));
      setInputValue({});

      const newComment = {
        user: userProfile,
        comment: inputValue.comment,
        createdAt: new Date(),
        _id: new Date(),
      };
      setCommentsList((prev) => ({
        ...prev,
        records: [newComment, ...(prev.records || [])],
        totalCount: prev.totalCount + 1,
      }));
    }
  };

  const handleBookmark = async () => {
    if (!isAuthenticated) return handleLoginPop(true);
    setUserAction({ action: "bookmarkProgress" });
    setStoryDetails((prev) => ({ ...prev, isBookmarked: !prev.isBookmarked }));

    const response = await handleApiRequest(manageBookmark, { storyId });
    if (response.status) {
      setUserAction({ action: "bookmarkSuccess", isBookmarked: !storyDetails.isBookmarked });
    } else {
      setStoryDetails((prev) => ({ ...prev, isBookmarked: !prev.isBookmarked }));
      setUserAction(null);
    }
  };

  const handleCommentReply = async () => {
    if (!isAuthenticated) return handleLoginPop(true);

    if (inputValue.reply?.length < 10)
      return setErrors((prev) => ({ ...prev, reply: "Must be atleast 10 charater long" }));
    else if (inputValue.reply?.length > maxReplyLength)
      return setErrors((prev) => ({
        ...prev,
        reply: `Maximum ${maxReplyLength} characters allowed`,
      }));
    else {
      setErrors({});
    }
    setUserAction({ action: "showReplyLoader" });

    const request = { storyId, commentId: addReply, reply: inputValue.reply };
    const response = await handleApiRequest(addCommentReply, request);
    setUserAction(null);

    if (response.status) {
      setCommentsList((prev) => {
        const oldComments = prev.records || [];
        const index = oldComments.findIndex((item) => item._id === addReply);

        if (index >= 0) {
          const oldComment = prev.records?.[index];
          const newReply = {
            _id: new Date(),
            comment: oldComment._id,
            reply: inputValue.reply,
            createdAt: new Date(),
            user: {
              _id: userProfile._id,
              name: userProfile.name,
              gender: userProfile.gender,
              avatar: userProfile.avatar,
            },
          };

          oldComments.splice(index, 1, {
            ...oldComment,
            repliesCount: oldComment.repliesCount + 1,
            replies: [newReply, ...(oldComment.replies || [])],
          });
        }

        return { records: oldComments, totalCount: prev.totalCount };
      });

      setInputValue({});
      setAddReply("");
    }
  };

  const handleRepliesList = async (commentId) => {
    const index = commentsList.records?.findIndex((item) => item._id === commentId);
    if (index < 0) return errorMsg("Something wrong happend");
    setUserAction({ action: "loadingReplies" });

    const repliesCount = isArray(commentsList.records?.[index]?.replies).length;
    let [page, limit] = [1, 10];

    for (let j = 1; j <= repliesCount; j++) {
      const possibleLimit = repliesCount / (j - 1);
      if (Number.isInteger(possibleLimit) && possibleLimit <= 20) {
        [page, limit] = [j, possibleLimit];
        break;
      }
    }

    const request = { commentId, orderBy: "createdAt", order: -1, page, limit };
    const response = await handleApiRequest(getRepliesList, request);
    setUserAction(null);

    if (response.status) {
      setCommentsList((prev) => {
        const oldComments = prev.records;
        const oldComment = oldComments[index];
        (oldComment.replies || []).push(...isArray(response.data?.records));

        oldComments.splice(index, 1, oldComment);
        return { records: oldComments, totalCount: prev.totalCount };
      });
    }
  };

  const handleUpdateCommentList = (commentId, reaction) => {
    setCommentsList((prev) => {
      const oldComments = prev.records;
      const index = oldComments?.findIndex((item) => item._id === commentId);
      if (index < 0) return prev;

      const oldComment = oldComments[index];

      let likesCount = oldComment.likesCount || 0;
      if (oldComment.myReaction) {
        if (oldComment.myReaction === reaction) {
          likesCount += reaction === "like" ? -1 : +1;
        } else {
          likesCount += reaction === "like" ? +2 : -2;
        }
      } else {
        likesCount += reaction === "like" ? +1 : -1;
      }
      const myReaction = oldComment.myReaction === reaction ? "" : reaction;

      oldComments.splice(index, 1, {
        ...oldComment,
        likesCount: likesCount,
        myReaction: myReaction,
      });

      return { records: oldComments, totalCount: prev.totalCount };
    });
  };

  const handleCommentReaction = async (commentId, reaction) => {
    if (!isAuthenticated) return handleLoginPop(true);

    handleUpdateCommentList(commentId, reaction);

    const request = {
      storyId: storyDetails._id,
      commentId,
      reaction: reaction,
    };

    const response = await handleApiRequest(manageCommentReaction, request);
    if (!response.status) {
      handleUpdateCommentList(commentId, reaction);
    }
  };

  useEffect(() => {
    if (storyId) {
      handleStoryDetails();
      handleCommentList();
    }

    return () => {
      setStoryDetails({});
    };
  }, [storyId]);

  return (
    <>
      {storyDetails.title && (
        <MyHelmet
          title={getSlug(storyDetails.title)}
          keywords={[]}
          description={htmlToText(storyDetails.description)?.slice(0, 150)}
          link={`${storyDetailsRoute}/${getSlug(storyDetails.title)}/${storyDetails._id}`}
        />
      )}

      <section className="storyDetailsContainer">
        <div className="py-4 px-3 px-lg-5">
          {storyDetails._id ? (
            <>
              <div className="d-flex align-items-center justify-content-between">
                <Link
                  to={
                    !storyDetails.anonymousSharing
                      ? `${otherUserProfile}/${getSlug(storyDetails.user?.name)}/${
                          storyDetails.user?._id
                        }`
                      : ""
                  }
                  className="mainStoryCardHeader text-color-unset text-decoration-none pointer d-flex align-items-center">
                  <img
                    className="img-medium img-fluid rounded-circle"
                    alt={storyDetails.anonymousSharing ? "userImage" : storyDetails.user?.name}
                    src={
                      storyDetails.anonymousSharing
                        ? getUserImage()
                        : getUserImage(storyDetails.user)
                    }
                    onError={() => {
                      setStoryDetails((prev) => {
                        const updatedUser = { ...prev.user, avatar: { url: maleAvatarHolder } };
                        return {
                          ...prev,
                          user: updatedUser,
                        };
                      });
                    }}
                  />
                  <div className="ms-2">
                    <p className="my-0">
                      {storyDetails.anonymousSharing ? "Anonymous" : storyDetails.user?.name}
                    </p>
                    <p className="muted small my-0">
                      Posted At: {format_date(storyDetails.createdAt)}
                    </p>
                  </div>
                </Link>

                {storyDetails.user !== userProfile._id && (
                  <Dropdown>
                    <Dropdown.Toggle className="border-secondary-light bg-header">
                      <p className="m-0 d-flex py-1 text-dark">
                        <HiDotsVertical />
                      </p>
                    </Dropdown.Toggle>

                    <Dropdown.Menu align="end">
                      <Dropdown.Item
                        as="p"
                        className="pointer m-0 px-2 py-1 d-flex align-items-center"
                        onClick={handleBookmark}>
                        {storyDetails?.isBookmarked ? (
                          <>
                            <FaBookmark className="me-2" />
                            Unsave
                          </>
                        ) : (
                          <>
                            <FaRegBookmark className="me-2" />
                            Save
                          </>
                        )}
                      </Dropdown.Item>
                      <Dropdown.Item
                        as="p"
                        className="pointer m-0 px-2 py-1 d-flex align-items-center"
                        onClick={() => {
                          if (!isAuthenticated) return handleLoginPop(true);
                          setUserAction({ action: "reportStory", id: storyId });
                        }}>
                        <FaFlag className="me-2" />
                        Report Story
                      </Dropdown.Item>
                      {!storyDetails.anonymousSharing && (
                        <>
                          <Dropdown.Item
                            as="p"
                            className="pointer m-0 px-2 py-1 d-flex align-items-center"
                            onClick={() => {
                              navigate(
                                `${otherUserProfile}/${getSlug(storyDetails.user?.name)}/${
                                  storyDetails.user?._id
                                }`
                              );
                            }}>
                            <FaCircleUser className="me-2" />
                            Visit Profile
                          </Dropdown.Item>
                          <Dropdown.Item
                            as="p"
                            className="pointer m-0 px-2 py-1 d-flex align-items-center"
                            onClick={() => {
                              if (!isAuthenticated) return handleLoginPop(true);
                              navigate(
                                `${privateRoutes.chatRoute}?receiver=${storyDetails.user._id}`
                              );
                            }}>
                            <FaCommentDots className="me-2" />
                            Message User
                          </Dropdown.Item>
                        </>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>

              <h1 className="h4 my-3">{storyDetails.title}</h1>
              <div className="storyDesc-dark storyDetailDesc">
                <p dangerouslySetInnerHTML={{ __html: storyDetails.description }} />
              </div>
            </>
          ) : (
            <StoryDetailsSkeleton className="py-0 px-0 px-lg-0" />
          )}

          <div className="storyAction d-flex align-items-center justify-content-start mb-3 gap-2">
            <p className="min-w-70 m-0 bg-header border-secondary-light px-2 pe-2 pe-sm-3 py-1 py-2 rounded-pill text-small d-flex align-items-center justify-content-center gap-1">
              <span
                dangerouslySetInnerHTML={{ __html: storyDetails.category?.iconFilled }}
                className="reactedIcon"
              />
              <span className="d-none d-sm-inline-block">{storyDetails.category?.name}</span>
            </p>
            <div className="position-relative">
              <AddReaction
                setStoryDetails={setStoryDetails}
                savedReaction={storyDetails.myReaction}
                reactionsCount={storyDetails?.reactionsCount}
                storyId={storyDetails._id}
                position="left"
              />
            </div>
            <p
              className="min-w-70 m-0 bg-header border-secondary-light px-2 pe-2 pe-sm-3 py-1 py-2 rounded-pill text-small d-flex align-items-center justify-content-center"
              onClick={() => {
                setUserAction({ action: "showSharePop" });
              }}>
              <span className="reactedIcon">
                <IoIosShareAlt className="width-22" />
              </span>
            </p>
          </div>

          <div>
            <textarea
              className="myInput border border-secondary-light form-control mt-4"
              placeholder="Share your thoughts"
              name="comment"
              rows={3}
              value={inputValue.comment || ""}
              onChange={handleChange}
            />
            <p className="m-0 text-small text-end mt-1 text-color">
              {inputValue.comment?.length || 0}/{maxCommentLength}
            </p>
          </div>
          <div className="d-flex justify-content-between">
            <div>
              <ErrorField error={errors.comment} />
            </div>
            <button
              className="primaryBtn min-w-150 rounded mt-2 px-3 py-1"
              disabled={!inputValue.comment || userAction?.action === "showCommentLoader"}
              onClick={handleAddComment}>
              {userAction?.action === "showCommentLoader" ? (
                <i className="growLoader spinner-grow ms-2" role="status" />
              ) : (
                "Add Comment"
              )}
            </button>
          </div>
        </div>

        <div className="curvatureContainer border-0">
          <div className="bg-white">
            <div id="curved-corner-topright" className="bg-white" />
          </div>
          <div className="bg-white">
            <div id="curved-corner-topleft" className="bg-white" />
          </div>
        </div>

        <div className="pb-5 px-3 px-lg-5">
          <h4 className="mb-3">Comments ({storyDetails.commentsCount || 0})</h4>

          {commentsList.totalCount ? (
            <>
              {commentsList.records?.map((comment, i) => {
                return (
                  <div key={comment?._id} className="shadow px-3 px-lg-4 py-3 mb-3">
                    <div className="d-flex align-items-center">
                      <img
                        src={getUserImage(comment.user)}
                        alt={comment.user?.name}
                        onError={(e) => {
                          e.target.src = getUserImage();
                        }}
                        className="img-small img-fluid rounded-circle me-2"
                      />
                      <div className="lh-sm">
                        <p className="my-0">{comment.user?.name}</p>
                        <p className="muted small my-0">
                          Comment At: {getTimePassed(comment.createdAt)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3">
                      {comment.comment}
                      <div className="storyAction text-color gap-1 d-flex align-items-center justify-content-start mt-3">
                        <p
                          onClick={() =>
                            setAddReply((prev) => (prev === comment._id ? "" : comment._id))
                          }>
                          <span className="reactedIcon">
                            <RiReplyFill />
                          </span>
                          Reply {comment.repliesCount ? `(${comment.repliesCount})` : ""}
                        </p>
                        <p className="d-flex align-items-center justify-content-center gap-1">
                          <span
                            onClick={() => handleCommentReaction(comment._id, "like")}
                            className="reactedIcon">
                            {comment.myReaction === "like" ? (
                              <TbArrowBigUpFilled />
                            ) : (
                              <TbArrowBigUp />
                            )}
                          </span>
                          <span className="margin-top-2">
                            {formatReactionCount(comment.likesCount) || 0}
                          </span>
                          <span
                            onClick={() => handleCommentReaction(comment._id, "dislike")}
                            className="reactedIcon margin-top-2">
                            {comment.myReaction === "dislike" ? (
                              <TbArrowBigDownFilled />
                            ) : (
                              <TbArrowBigDown />
                            )}
                          </span>
                        </p>
                      </div>

                      {addReply === comment._id && (
                        <>
                          <div>
                            <textarea
                              className="myInput form-control mt-4"
                              placeholder="Add your reply"
                              name="reply"
                              value={inputValue.reply}
                              onChange={handleChange}
                            />
                            <p className="m-0 text-small text-end mt-1 text-color">
                              {inputValue.reply?.length || 0}/{maxReplyLength}
                            </p>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <div>
                              <ErrorField error={errors.reply} />
                            </div>
                            <button
                              className="primaryBtn min-w-70 rounded px-3 mt-2"
                              disabled={
                                !inputValue.reply || userAction?.action === "showReplyLoader"
                              }
                              onClick={handleCommentReply}>
                              {userAction?.action === "showReplyLoader" ? (
                                <>
                                  <i className="growLoader spinner-grow ms-2" role="status" />
                                </>
                              ) : (
                                "Reply"
                              )}
                            </button>
                          </div>
                        </>
                      )}

                      {comment.replies?.length > 0 && (
                        <div className="mt-3">
                          {comment.replies?.map((reply) => {
                            return (
                              <div
                                key={reply._id}
                                className="border border-secondary-light mb-2 mx-2 pt-2 px-2 rounded">
                                <div className="d-flex">
                                  <img
                                    src={getUserImage(reply.user)}
                                    alt={reply.user?.name}
                                    onError={(e) => {
                                      e.target.src = getUserImage();
                                    }}
                                    className="img-small img-fluid rounded-circle"
                                  />
                                  <div className="ms-2">
                                    <p className="my-0">{reply.user?.name}</p>
                                    <p className="text-small muted my-0">
                                      Replied At: {getTimePassed(reply.createdAt)}
                                    </p>
                                  </div>
                                </div>
                                <p className="lh-20 m-0 muted py-3"> {reply.reply}</p>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {comment.replies?.length > 0 &&
                        comment.replies?.length < comment.repliesCount && (
                          <div className="text-end">
                            <button
                              className="textBtn mx-2 mt-0"
                              disabled={userAction?.action === "loadingReplies"}
                              onClick={() => handleRepliesList(comment._id)}>
                              {userAction?.action === "loadingReplies" ? "Loading..." : "Load More"}
                            </button>
                          </div>
                        )}
                    </div>
                  </div>
                );
              })}
              <div className="d-flex justify-content-end">
                <MyPagination
                  paginationDetails={paginationDetails}
                  setPaginationDetails={setPaginationDetails}
                  totalCount={commentsList.totalCount}
                  darkPagination={true}
                />
              </div>
            </>
          ) : (
            <div className="d-flex justify-content-end">
              <img
                src="/images/beTheFirstToAddComment.webp"
                alt="addFirstComment"
                className="max-w-300 img-fluid rounded-5 mx-auto"
              />
            </div>
          )}
        </div>

        {/* 
        <div className="curvatureContainer lowerCurvatureContainer">
          <div>
            <div id="curved-corner-bottomright" className="curvedCorner" />
          </div>
          <div>
            <div id="curved-corner-bottomleft" className="curvedCorner" />
          </div>
        </div> */}
      </section>

      {userAction?.action === "showSharePop" && (
        <SharePop userAction={userAction} setUserAction={setUserAction} />
      )}

      {(userAction?.action === "bookmarkSuccess" || userAction?.action === "bookmarkProgress") && (
        <BookmarkSuccess userAction={userAction} setUserAction={setUserAction} />
      )}
      {userAction?.action === "reportStory" && (
        <ReportStoryPop userAction={userAction} setUserAction={setUserAction} />
      )}
    </>
  );
}
